// @moods/ui copy. Every section is split per language (sections/<name>.tr.ts / .en.ts, identical keys): Turkish —
// the default UI language — is bundled, English is its own chunk loaded on demand (`loadUiLocale`; the provider
// does it when the locale switches), so Turkish users never download it (Phase 14 budget, spec §15).
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import base from './sections/base.tr.ts';
import identity from './sections/identity.tr.ts';
import nav from './sections/nav.tr.ts';
import chat from './sections/chat.tr.ts';
import voice from './sections/voice.tr.ts';
import cards from './sections/cards.tr.ts';
import media from './sections/media.tr.ts';
import profile from './sections/profile.tr.ts';
import discussions from './sections/discussions.tr.ts';
import notifications from './sections/notifications.tr.ts';

export type UiLocale = 'tr' | 'en';
export type UiDict = Record<string, string>;
export interface UiSection {
  tr: UiDict;
  en: UiDict;
}

const TR: UiDict = Object.assign({}, base, identity, nav, chat, voice, cards, media, profile, discussions, notifications);

/** Loaded dictionaries: `tr` always, `en` after `loadUiLocale('en')`. */
export const UI_MESSAGES: { tr: UiDict; en?: UiDict } = { tr: TR };

let enLoading: Promise<void> | null = null;

/** Loads a locale's copy (no-op for Turkish or when already loaded). */
export function loadUiLocale(locale: UiLocale): Promise<void> {
  if (locale === 'tr' || UI_MESSAGES.en) return Promise.resolve();
  enLoading ??= import('./en.ts').then(
    m => {
      UI_MESSAGES.en = m.UI_EN;
    },
    (err: unknown) => {
      enLoading = null;
      throw err;
    },
  );
  return enLoading;
}

/** Static builds that render English up front (the marketing site) register the dictionary synchronously. */
export function registerUiLocale(locale: 'en', dict: UiDict): void {
  UI_MESSAGES[locale] = dict;
}

const dictFor = (locale: UiLocale): UiDict => (locale === 'en' ? (UI_MESSAGES.en ?? TR) : TR);

const LocaleContext = createContext<{ locale: UiLocale; dict: UiDict }>({ locale: 'tr', dict: TR });

/** Sets the locale used by @moods/ui's built-in copy (labels, aria-labels, relative texts). */
export function UiLocaleProvider({ locale, children }: { locale: UiLocale; children: ReactNode }) {
  const [dict, setDict] = useState<UiDict>(() => dictFor(locale));
  useEffect(() => {
    let live = true;
    setDict(dictFor(locale));
    if (locale !== 'tr' && !UI_MESSAGES.en) {
      loadUiLocale(locale).then(
        () => live && setDict(dictFor(locale)),
        () => undefined, // offline: Turkish copy stays
      );
    }
    return () => {
      live = false;
    };
  }, [locale]);
  const value = useMemo(() => ({ locale, dict }), [locale, dict]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k: string) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

export type UiT = (key: string, vars?: Record<string, string | number>) => string;

/** Translate a @moods/ui message key: `t('call.mute')`, `t('typing.one', { name })`. Falls back to Turkish. */
export function useUiT(): UiT {
  const { dict } = useContext(LocaleContext);
  return useCallback((key, vars) => interpolate(dict[key] ?? TR[key] ?? key, vars), [dict]);
}

export function useUiLocale(): UiLocale {
  return useContext(LocaleContext).locale;
}
